import { useEnvironment } from './environment.ts'

/**
 * The environment, as a word, wherever the owner can see it.
 *
 * §5.1 states it "in the masthead, always" — and the sign-in page has no masthead, which
 * is exactly the page where knowing whether this tab is prod matters before anything is
 * typed. So it is one badge, used in both places.
 *
 * It is a word rather than only a colour (§21), and a failed read says so rather than
 * guessing (§5.1).
 *
 * **It owns the rail as well as the word.** The rail is the three pixels along the top
 * edge of the viewport, in the estate's own colour, and it lives here rather than in the
 * shell for two reasons: this component is the one place that knows which estate this is,
 * and it is rendered exactly once on every page — inside the masthead when signed in,
 * on its own above the card when signed out — so the rail is drawn exactly once too,
 * including on the page where there is no masthead to hang it from.
 *
 * The colour is chosen in CSS from `data-environment`, so this component grows no branch
 * per estate and the word remains the whole of its text content.
 */
export function EnvironmentBadge() {
  const environment = useEnvironment()

  return (
    <p
      className={environment.known ? 'masthead-environment' : 'masthead-environment unknown'}
      data-environment={environment.label}
      data-testid="environment"
    >
      <span className="env-rail" aria-hidden="true" />
      {environment.label}
    </p>
  )
}

/**
 * The estate as a word with its colour, for a place that is not the masthead — a card that says
 * which deployment its facts are about (Your account's "This deployment"). The same look as the
 * masthead's badge, without the rail (there is exactly one rail on a page) and without its test
 * id (there is exactly one masthead badge on a page).
 */
export function EnvironmentPill() {
  const environment = useEnvironment()

  return (
    <span
      className={environment.known ? 'masthead-environment' : 'masthead-environment unknown'}
      data-environment={environment.label}
    >
      {environment.label}
    </span>
  )
}
